import Head from 'next/head'
import { useEffect, useRef } from 'react'
import {
  ArrowRight,
  Blocks,
  Bot,
  PanelsTopLeft,
  Check,
  CircuitBoard,
  Database,
  Layers3,
  LineChart,
} from 'lucide-react'
import { buttonClassName } from '../components/ui/button'
import { SiteFooter, SiteHeader } from '../components/ui/site-chrome'

const pricingTiers = [
  {
    name: 'Circuit Audit & Blueprint',
    price: '$25,000',
    eyebrow: 'Diagnostic engagement',
    timeline: '2-4 weeks',
    description:
      'A precise map of where energy is leaking across technology, teams, process, data, vendors, and AI initiatives.',
    bestFor: 'Founders and operators who need clarity before committing to another roadmap, vendor, rebuild, or AI push.',
    outcomes: [
      'Circuit Blueprint across people, process, platforms, and data',
      'Prioritized repair roadmap with cost, risk, and business impact',
      'Executive readout your internal team can own',
      'Optional next-step implementation plan',
    ],
  },
  {
    name: 'Full Circuit Transformation',
    price: '$250,000',
    eyebrow: 'Hands-on implementation',
    timeline: '3-6 months',
    description:
      'A deep fractional CTO partnership where we diagnose the system, then rewire the workflows, architecture, and delivery habits that slow growth.',
    bestFor: 'Companies ready to fix the operational engine instead of buying more tools or hiring blindly.',
    outcomes: [
      'Everything in the Circuit Audit & Blueprint',
      'Embedded fractional CTO leadership',
      'Workflow, team, and architecture remediation',
      'Knowledge transfer so the team owns the new system',
    ],
    featured: true,
  },
  {
    name: 'Scale-Ready Systems Partnership',
    price: '$1,000,000',
    eyebrow: 'Strategic systems buildout',
    timeline: '12-24 months',
    description:
      'Long-range technical and organizational architecture for companies preparing for funding, acquisition, new markets, or major product expansion.',
    bestFor: 'Visionary founders building a company that needs durable technical leadership, talent design, and innovation systems.',
    outcomes: [
      'Everything in the Full Circuit Transformation',
      'Multi-year technology and systems strategy',
      'Technical leadership hiring and org design',
      'Scale-ready operating model for 10x capacity',
    ],
  },
]

const workshopTiers = [
  {
    name: 'AI Operator Workshop',
    price: '$600-$1,200 / seat',
    detail: 'Live cohort for founders, operators, and technical teams learning practical AI workflows.',
  },
  {
    name: 'Canonical Agents Lab',
    price: '$1,500-$2,800 / seat',
    detail:
      'Premium implementation lab for contextual agents, memory blocks, orchestration, schemas, and production patterns.',
  },
  {
    name: 'Private Team Workshop',
    price: 'From $7,500',
    detail:
      'Custom session for startup or mid-market teams with a company-specific use case, templates, and executive readout.',
  },
]

const productFeatures = [
  {
    icon: PanelsTopLeft,
    title: 'Browser extension beachhead',
    copy:
      'A panel that can inspect multiple property listing tabs and evaluate each deal without forcing investors out of their existing workflow.',
  },
  {
    icon: Bot,
    title: 'Composable agent orchestra',
    copy:
      'Deal Underwriter, Portfolio Risk, and Diligence agents read/write shared context blocks instead of dumping everything into one prompt.',
  },
  {
    icon: LineChart,
    title: 'Heavy calculation artifacts',
    copy:
      'Python-style pro formas, DSCR, IRR, cash flow, sensitivity, pricing matches, and green-light/red-light investment labels.',
  },
  {
    icon: Database,
    title: 'Canonical investor memory',
    copy:
      'Buy box, portfolio, preferences, current deal, document analysis, and conversation state stay versioned and reusable.',
  },
]

const productPilot = [
  'Extension-first prototype for Zillow, Redfin, Silo, LoopNet, or investor listing workflows',
  'SaaS dashboard for portfolio, stored deals, reports, and buy-box configuration',
  'Agent schemas for Underwriting, Portfolio Risk, and Document Diligence',
  'Pilot strategy across freemium, Pro, Team, and future API/data-layer pricing',
]

export default function Pricing() {
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let context: { revert: () => void } | undefined

    const runAnimation = async () => {
      const gsapModule = await import('gsap')
      const scrollTriggerModule = await import('gsap/ScrollTrigger')
      const gsap = gsapModule.gsap
      const ScrollTrigger = scrollTriggerModule.ScrollTrigger

      gsap.registerPlugin(ScrollTrigger)

      context = gsap.context(() => {
        gsap.from('.pricing-hero-copy > *', {
          y: 28,
          opacity: 0,
          duration: 0.8,
          ease: 'power3.out',
          stagger: 0.1,
        })

        gsap.utils.toArray<HTMLElement>('.pricing-reveal').forEach((element) => {
          gsap.from(element, {
            y: 46,
            opacity: 0,
            duration: 0.75,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: element,
              start: 'top 84%',
            },
          })
        })

        gsap.to('.pricing-orbit', {
          rotate: 360,
          duration: 28,
          ease: 'none',
          repeat: -1,
        })

        gsap.to('.pricing-product-panel', {
          yPercent: -8,
          ease: 'none',
          scrollTrigger: {
            trigger: '.pricing-product-section',
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        })
      }, rootRef)
    }

    runAnimation()

    return () => {
      context?.revert()
    }
  }, [])

  return (
    <div ref={rootRef} className="min-h-screen overflow-hidden bg-slate-950 text-slate-100">
      <Head>
        <title>Zadro Pricing | Fractional CTO, AI Workshops & Product Innovation</title>
        <meta
          name="description"
          content="Explore Zadro pricing for Circuit Audits, fractional CTO transformation, scale-ready systems, AI workshops, and Product Innovator pilots like Deal Lab."
        />
      </Head>

      <SiteHeader current="/pricing" />

      <main>
        <section className="relative border-b border-slate-800">
          <div className="absolute inset-0 circuit-grid opacity-40" aria-hidden="true" />
          <div className="relative mx-auto grid min-h-[78vh] max-w-7xl items-center gap-12 px-5 py-20 sm:px-8 lg:grid-cols-[1fr_0.85fr]">
            <div className="pricing-hero-copy max-w-4xl">
              <p className="section-kicker">Pricing Models</p>
              <h1 className="mt-5 text-5xl font-black leading-[0.96] tracking-tight text-white sm:text-6xl lg:text-7xl">
                Choose the level of wiring your company is ready to fix.
              </h1>
              <p className="mt-7 max-w-2xl text-lg leading-8 text-slate-300">
                Pricing is framed around depth of transformation: clarity, implementation, scale, and
                product innovation. The goal is not hours. The goal is permanent infrastructure,
                ownership, and leverage.
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <a
                  href="/book"
                  className={buttonClassName({ size: 'lg' })}
                >
                  Start with a diagnostic
                  <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href="#product-innovator"
                  className={buttonClassName({ size: 'lg', variant: 'outline' })}
                >
                  View Product Innovator
                </a>
              </div>
            </div>

            <div className="pricing-reveal relative mx-auto w-full max-w-md">
              <div className="overflow-hidden rounded-[1.5rem] border border-slate-800 bg-slate-950 shadow-[0_24px_80px_rgba(2,6,23,0.42)]">
                <img
                  src="/header-images/compute-diagnostic.svg"
                  alt="Pricing engagement ladder visualization"
                  className="h-64 w-full object-cover"
                />
                <div className="border-t border-slate-800 p-6">
                  <div className="flex items-center gap-3">
                    <CircuitBoard className="h-10 w-10 text-sky-300" />
                    <div>
                      <p className="font-mono text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
                        Engagement Ladder
                      </p>
                      <p className="text-3xl font-black text-white">$25k → $1M</p>
                    </div>
                  </div>
                  <p className="mt-4 text-sm leading-6 text-slate-300">
                    Diagnose, transform, scale, then productize the advantage with the right level of
                    technical wiring.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <div className="pricing-reveal max-w-3xl">
            <p className="section-kicker">Core Engagements</p>
            <h2 className="section-title">Fractional CTO pricing for real business constraints.</h2>
          </div>

          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {pricingTiers.map((tier) => (
              <article
                key={tier.name}
                className={`pricing-reveal rounded-[1.5rem] border p-6 shadow-[0_18px_60px_rgba(2,6,23,0.24)] ${
                  tier.featured
                    ? 'border-sky-300 bg-sky-300 text-slate-950 shadow-2xl shadow-sky-400/20'
                    : 'border-slate-800 bg-slate-900 text-slate-100'
                }`}
              >
                <p
                  className={`font-mono text-sm font-semibold uppercase tracking-[0.18em] ${
                    tier.featured ? 'text-slate-700' : 'text-sky-300'
                  }`}
                >
                  {tier.eyebrow}
                </p>
                <h3 className="mt-4 text-2xl font-black">{tier.name}</h3>
                <p className="mt-4 text-4xl font-black">{tier.price}</p>
                <p className={`mt-2 text-sm font-semibold ${tier.featured ? 'text-slate-700' : 'text-slate-400'}`}>
                  {tier.timeline}
                </p>
                <p className={`mt-5 leading-7 ${tier.featured ? 'text-slate-800' : 'text-slate-300'}`}>
                  {tier.description}
                </p>
                <div className={`mt-6 rounded-2xl p-4 ${tier.featured ? 'bg-slate-950/10' : 'bg-slate-950/70'}`}>
                  <p className="text-sm font-bold">Best for</p>
                  <p className={`mt-2 text-sm leading-6 ${tier.featured ? 'text-slate-800' : 'text-slate-300'}`}>
                    {tier.bestFor}
                  </p>
                </div>
                <ul className="mt-6 space-y-3 text-sm">
                  {tier.outcomes.map((item) => (
                    <li key={item} className="flex gap-3">
                      <Check className={`mt-0.5 h-4 w-4 flex-none ${tier.featured ? 'text-slate-950' : 'text-sky-300'}`} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section id="product-innovator" className="pricing-product-section border-y border-slate-800 bg-slate-900/60">
          <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 sm:px-8 lg:grid-cols-[0.9fr_1.1fr]">
            <div className="pricing-reveal">
              <p className="section-kicker">Product Innovator</p>
              <h2 className="section-title">Deal Lab Pilot: AI agents for real estate investors.</h2>
              <p className="mt-5 leading-7 text-slate-300">
                This is the innovation track: we build product pilots that can later become standalone
                SaaS, extensions, APIs, or internal tools. Deal Lab is the running example: a Silo-style
                C-estimator that reads multiple property listing tabs, evaluates fit against an investor
                portfolio, and returns green-light/red-light underwriting with pricing formulas.
              </p>
              <div className="mt-8 rounded-[1.5rem] border border-slate-800 bg-slate-950 p-6">
                <p className="font-mono text-sm font-semibold uppercase tracking-[0.18em] text-sky-300">
                  Pilot investment
                </p>
                <p className="mt-3 text-4xl font-black text-white">$75k-$150k</p>
                <p className="mt-3 leading-7 text-slate-300">
                  Discovery, schema design, prototype, agent orchestration, extension UX, and investor
                  pilot roadmap. Production SaaS buildouts are scoped separately.
                </p>
              </div>
            </div>

            <div className="pricing-product-panel rounded-[1.5rem] border border-slate-800 bg-slate-950 p-6">
              <div className="grid gap-4 sm:grid-cols-2">
                {productFeatures.map((feature) => {
                  const Icon = feature.icon
                  return (
                    <article key={feature.title} className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
                      <Icon className="h-6 w-6 text-sky-300" />
                      <h3 className="mt-4 text-xl font-bold text-white">{feature.title}</h3>
                      <p className="mt-3 text-sm leading-6 text-slate-300">{feature.copy}</p>
                    </article>
                  )
                })}
              </div>
            </div>
          </div>

          <div className="mx-auto max-w-7xl px-5 pb-20 sm:px-8">
            <div className="pricing-reveal rounded-[1.5rem] border border-slate-800 bg-slate-950 p-6">
              <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr]">
                <div>
                  <p className="section-kicker">What It Includes</p>
                  <h3 className="mt-4 text-3xl font-black text-white">From consulting artifact to product wedge.</h3>
                </div>
                <ul className="grid gap-3 sm:grid-cols-2">
                  {productPilot.map((item) => (
                    <li key={item} className="flex gap-3 rounded-2xl border border-slate-800 bg-slate-900 p-4 text-sm leading-6 text-slate-300">
                      <Blocks className="mt-0.5 h-4 w-4 flex-none text-sky-300" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <div className="pricing-reveal max-w-3xl">
            <p className="section-kicker">Workshops</p>
            <h2 className="section-title">Training priced for implementation, not passive attendance.</h2>
          </div>
          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {workshopTiers.map((tier) => (
              <article key={tier.name} className="pricing-reveal rounded-[1.5rem] border border-slate-800 bg-slate-900 p-6 shadow-[0_18px_60px_rgba(2,6,23,0.24)]">
                <Layers3 className="h-6 w-6 text-sky-300" />
                <h3 className="mt-4 text-2xl font-black text-white">{tier.name}</h3>
                <p className="mt-3 text-2xl font-black text-sky-300">{tier.price}</p>
                <p className="mt-4 leading-7 text-slate-300">{tier.detail}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="border-t border-slate-800">
          <div className="mx-auto grid max-w-7xl gap-8 px-5 py-20 sm:px-8 lg:grid-cols-[1fr_auto] lg:items-end">
            <div className="pricing-reveal">
              <p className="section-kicker">Next Step</p>
              <h2 className="section-title max-w-3xl">Start by choosing the constraint you want to solve first.</h2>
              <p className="mt-5 max-w-2xl leading-7 text-slate-300">
                Bring the business problem, product idea, platform bottleneck, or AI workflow. We will
                map the right engagement model and decide whether this is an audit, transformation,
                workshop, or product innovation pilot.
              </p>
            </div>
            <a
              href="/book"
              className={buttonClassName({ className: 'pricing-reveal', size: 'lg' })}
            >
              Book pricing conversation
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  )
}
